import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

interface SmartwatchHeroMockupProps {
    isRtl?: boolean;
}

const SmartwatchHeroMockup: React.FC<SmartwatchHeroMockupProps> = ({ isRtl = true }) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language;
    const isFa = currentLang === "fa" || isRtl;

    // Convert numbers to Persian digits if in Persian
    const toPersianDigits = (n: number | string) => {
        const farsiDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
        return n.toString().replace(/\d/g, (x) => farsiDigits[parseInt(x, 10)]);
    };

    const [progress, setProgress] = useState(42);

    // Subtle realistic progress animation simulating live download
    useEffect(() => {
        const timer = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 99) return 42;
                return prev + 1;
            });
        }, 1200);
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="relative w-full max-w-[440px] sm:max-w-[480px] lg:max-w-[520px] mx-auto flex items-center justify-center py-4 sm:py-8 select-none">
            {/* ── Centered Glowing SVG Circle & Aura directly centered behind the Phone ── */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[460px] sm:w-[560px] lg:w-[640px] h-[460px] sm:h-[560px] lg:h-[640px] -z-10 pointer-events-none flex items-center justify-center">
                {/* SVG Glow Circle matching user's custom blue gradient & blur */}
                <svg
                    className="w-full h-full filter drop-shadow-[0_25px_60px_rgba(12,102,228,0.45)]"
                    viewBox="0 0 800 800"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <g filter="url(#phone_bg_blur)">
                        <circle cx="400" cy="400" r="290" fill="url(#phone_bg_radial)" opacity="0.9" />
                    </g>
                    {/* Concentric subtle outer aura circle */}
                    <circle cx="400" cy="400" r="310" stroke="url(#phone_bg_radial)" strokeWidth="3" opacity="0.25" />
                    <circle cx="400" cy="400" r="250" fill="url(#phone_bg_radial)" opacity="0.2" />
                    <defs>
                        <filter id="phone_bg_blur" x="0" y="0" width="800" height="800" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                            <feGaussianBlur stdDeviation="110" result="blur" />
                        </filter>
                        <radialGradient id="phone_bg_radial" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(400 400) rotate(90) scale(290)">
                            <stop stopColor="#0C66E4" />
                            <stop offset="0.55" stopColor="#155DFC" stopOpacity="0.85" />
                            <stop offset="1" stopColor="#579DFF" stopOpacity="0" />
                        </radialGradient>
                    </defs>
                </svg>
            </div>

            {/* Smartphone Assembly with Subtle Floating Motion */}
            <motion.div
                className="relative flex flex-col items-center justify-center"
                initial={{ y: 0 }}
                animate={{ y: [-4, 4, -4] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            >
                {/* ── Outer Smartphone Frame (iPhone Titanium Chassis with Drop Shadow) ── */}
                <div className="relative p-[10px] sm:p-[12px] bg-gradient-to-b from-[#3A3A3C] via-[#1C1C1E] to-[#121214] rounded-[52px] sm:rounded-[58px] shadow-[0_30px_70px_-15px_rgba(12,102,228,0.45),0_15px_30px_-10px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.18),inset_0_1px_2px_rgba(255,255,255,0.3)] border border-[#2C2C2E]">
                    
                    {/* Left Hardware Buttons (Volume & Action) */}
                    <div className="absolute -left-[3px] top-[115px] w-[3px] h-[24px] bg-[#2C2C2E] rounded-l-sm" />
                    <div className="absolute -left-[3px] top-[155px] w-[3px] h-[46px] bg-[#2C2C2E] rounded-l-sm" />
                    <div className="absolute -left-[3px] top-[215px] w-[3px] h-[46px] bg-[#2C2C2E] rounded-l-sm" />

                    {/* Right Hardware Button (Power / Side key) */}
                    <div className="absolute -right-[3px] top-[160px] w-[3px] h-[68px] bg-[#2C2C2E] rounded-r-sm" />

                    {/* Antenna Bands on side frame */}
                    <div className="absolute -left-[1px] top-[75px] w-[2px] h-[4px] bg-[#555558] opacity-60" />
                    <div className="absolute -left-[1px] bottom-[75px] w-[2px] h-[4px] bg-[#555558] opacity-60" />
                    <div className="absolute -right-[1px] top-[75px] w-[2px] h-[4px] bg-[#555558] opacity-60" />
                    <div className="absolute -right-[1px] bottom-[75px] w-[2px] h-[4px] bg-[#555558] opacity-60" />

                    {/* ── OLED Display Glass Surface ── */}
                    <div className="relative w-[280px] sm:w-[320px] h-[500px] sm:h-[560px] bg-[#07080B] rounded-[44px] sm:rounded-[48px] overflow-hidden flex flex-col justify-between border border-black shadow-[inset_0_0_20px_rgba(0,0,0,0.95)]">
                        
                        {/* ── Rich Wallpaper Background (Vibrant Glowing Rings + Deep Gradient Mesh) ── */}
                        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1021] via-[#080B16] to-[#04050A] overflow-hidden pointer-events-none">
                            {/* Central Glowing Mesh Aura behind Logo */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] sm:w-[320px] h-[280px] sm:h-[320px] rounded-full bg-gradient-to-tr from-[#155DFC]/25 via-blue-600/15 to-cyan-500/10 blur-2xl pointer-events-none" />

                            {/* Top Concentric Glass Ring Sphere */}
                            <div className="absolute top-[12%] left-1/2 -translate-x-1/2 w-[280px] sm:w-[320px] h-[280px] sm:h-[320px] rounded-full border border-blue-400/15 bg-gradient-to-b from-blue-500/10 via-[#141828]/40 to-transparent shadow-[inset_0_0_45px_rgba(21,93,252,0.2)]" />
                            
                            {/* Inner Ring Accent */}
                            <div className="absolute top-[16%] left-1/2 -translate-x-1/2 w-[220px] sm:w-[250px] h-[220px] sm:h-[250px] rounded-full border border-sky-300/10 bg-gradient-to-b from-white/[0.04] to-transparent pointer-events-none" />

                            {/* Bottom Ring Sphere */}
                            <div className="absolute bottom-[2%] left-1/2 -translate-x-1/2 w-[280px] sm:w-[320px] h-[280px] sm:h-[320px] rounded-full border border-blue-400/15 bg-gradient-to-t from-blue-600/15 via-[#141828]/40 to-transparent shadow-[inset_0_0_45px_rgba(21,93,252,0.2)]" />

                            {/* Center Depth Radial Shading */}
                            <div className="absolute inset-0 bg-radial from-transparent via-black/30 to-black/80 pointer-events-none" />

                            {/* Sapphire Glass Glare Reflection on top right */}
                            <div className="absolute -top-12 -right-12 w-64 h-64 bg-gradient-to-bl from-white/[0.1] via-white/[0.02] to-transparent rounded-full pointer-events-none z-20" />
                        </div>

                        {/* ── Dynamic Island / Download Live Activity Banner (Top Widget) ── */}
                        <div className="relative z-30 pt-3 sm:pt-4 px-3 sm:px-4 w-full">
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                                className="relative w-full rounded-[24px] sm:rounded-[28px] bg-[#0F172A]/95 border border-white/15 backdrop-blur-xl shadow-[0_16px_35px_rgba(0,0,0,0.7),0_0_20px_rgba(21,93,252,0.2)] overflow-hidden"
                            >
                                {/* Card Body */}
                                <div className="px-4 sm:px-5 pt-3.5 pb-2.5 flex flex-col items-center text-center">
                                    {/* Persian Title: دلبند در حال دانلود است! */}
                                    <h4 className="text-white text-[13.5px] sm:text-[15.5px] font-bold tracking-tight text-center leading-snug font-sans">
                                        {isFa ? "دلبند در حال دانلود است!" : "Delband is downloading!"}
                                    </h4>

                                    {/* Progress percentage: ۴۲٪ */}
                                    <div className="w-full flex justify-end items-center mt-1 px-1">
                                        <span className="text-[12px] sm:text-[13px] font-medium text-slate-300/90 font-sans tracking-wide">
                                            {isFa ? `${toPersianDigits(progress)}٪` : `${progress}%`}
                                        </span>
                                    </div>
                                </div>

                                {/* Active Bottom Neon Glow Progress Line */}
                                <div className="relative w-full h-[3px] bg-slate-800/80 overflow-hidden">
                                    {/* Progress Fill */}
                                    <div
                                        className="h-full bg-gradient-to-r from-[#155DFC] via-[#38BDF8] to-cyan-300 transition-all duration-300 ease-out relative"
                                        style={{ width: `${progress}%` }}
                                    >
                                        {/* Neon Glow Flare on progress head */}
                                        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-3 bg-cyan-300 blur-sm rounded-full" />
                                    </div>
                                </div>

                                {/* Ambient Blue Glow Radiating from Bottom of Island */}
                                <div className="absolute bottom-0 inset-x-8 h-4 bg-blue-500/30 blur-md pointer-events-none" />
                            </motion.div>
                        </div>

                        {/* ── Center Content Area: Enlarged App Icon & Title ── */}
                        <div className="relative z-20 my-auto flex flex-col items-center justify-center py-2">
                            {/* App Icon Glass Card - Enlarged with Vibrant Glow */}
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                transition={{ type: "spring", stiffness: 350, damping: 20 }}
                                className="relative w-36 h-36 sm:w-44 sm:h-44 rounded-[32px] sm:rounded-[38px] bg-gradient-to-b from-[#1E2538]/90 to-[#121624]/90 border border-white/20 shadow-[0_25px_50px_rgba(0,0,0,0.8),0_0_40px_rgba(21,93,252,0.35)] backdrop-blur-2xl flex items-center justify-center p-5 cursor-pointer"
                            >
                                {/* Inner Subtle Radial Sheen */}
                                <div className="absolute inset-0 rounded-[32px] sm:rounded-[38px] bg-gradient-to-br from-white/15 via-transparent to-black/50 pointer-events-none" />
                                
                                {/* Ambient Logo Halo */}
                                <div className="absolute w-28 h-28 sm:w-36 sm:h-36 rounded-full bg-[#155DFC]/35 blur-2xl -z-10" />

                                {/* ── Delband App Icon from public/media/Logo1.svg (Enlarged) ── */}
                                <img
                                    src="/media/Logo1.svg"
                                    alt="دلبند"
                                    className="w-24 h-24 sm:w-32 sm:h-32 drop-shadow-[0_8px_20px_rgba(21,93,252,0.5)] object-contain select-none"
                                />
                            </motion.div>

                            {/* App Name & Tagline under Logo to fill screen beautifully */}
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="mt-3 sm:mt-4 text-center px-4"
                            >
                                <h3 className="text-white text-lg sm:text-xl font-bold tracking-wide font-sans">
                                    {isFa ? "دلبند" : "Delband"}
                                </h3>
                                <p className="text-[11px] sm:text-[12.5px] text-sky-200/85 font-medium mt-0.5 font-sans">
                                    {isFa ? "دستیار هوشمند سلامت و خودمراقبتی" : "Smart Health & Self-Care Assistant"}
                                </p>
                            </motion.div>
                        </div>

                        {/* Bottom Home Indicator Bar */}
                        <div className="relative z-20 pb-2.5 flex justify-center items-center">
                            <div className="w-28 h-1 bg-white/35 rounded-full" />
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default SmartwatchHeroMockup;
